"use client";

import { useState, useRef, useEffect } from "react";
import { Globe } from "lucide-react";

const LANGUAGES = [
  { code: "id", label: "Bahasa Indonesia", flag: "🇮🇩" },
  { code: "en", label: "English", flag: "🇺🇸" },
];

function getLocale(): string {
  if (typeof window === "undefined") return "id";
  return (
    document.cookie
      .split("; ")
      .find((row) => row.startsWith("kustomer-locale="))
      ?.split("=")[1] || "id"
  );
}

function setLocale(locale: string) {
  document.cookie = `kustomer-locale=${locale};path=/;max-age=31536000`;
  window.location.reload();
}

export function LanguageSwitcher({ compact = false }: { compact?: boolean }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const currentLocale = getLocale();

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const current = LANGUAGES.find((l) => l.code === currentLocale) || LANGUAGES[0];

  return (
    <div ref={ref} className="relative">
      <button
        data-testid="language-switcher-btn"
        // QC-F09 — locale aktif ditegaskan lewat atribut, bukan lewat bendera
        // maupun teks: keduanya berubah antar locale dan tidak boleh jadi
        // satu-satunya cara skenario QC membuktikan bahasa mana yang aktif.
        data-locale={current.code}
        onClick={() => setOpen(!open)}
        className="flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
        title={current.label}
      >
        <Globe className="w-4 h-4" />
        {!compact && (
          <span>
            {current.flag} {current.code.toUpperCase()}
          </span>
        )}
      </button>

      {open && (
        <div
          data-testid="language-switcher-menu"
          className="absolute bottom-full left-0 mb-1 bg-white border border-gray-200 rounded-lg shadow-lg py-1 min-w-[180px] z-50"
        >
          {LANGUAGES.map((lang) => (
            <button
              key={lang.code}
              data-testid={`language-option-${lang.code}`}
              onClick={() => {
                setLocale(lang.code);
                setOpen(false);
              }}
              className={`w-full flex items-center gap-3 px-3 py-2 text-sm hover:bg-gray-100 transition-colors ${
                lang.code === currentLocale ? "text-blue-600 font-medium bg-blue-50" : "text-gray-700"
              }`}
            >
              <span>{lang.flag}</span>
              <span>{lang.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
